import React from 'react';
import { Link } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import { formatCaseNumber } from 'utils/caseNumber';
import { isTerminalCaseStatus, CaseStatus } from 'types/interfaces/case';

export interface SummonCaseState {
  caseNumber?: number | null;
  status: string;
  /** The thread the case lives in (the trade thread since change 2); absent on an older backend, which drops the link. */
  conversationId?: string | null;
}

interface Props {
  /** The trade's case, if any. */
  caseState: SummonCaseState | null | undefined;
  onSummon: () => void;
  disabled?: boolean;
  /** Where this control sits; the modal variant keeps its button inline. */
  layout?: 'stack' | 'inline';
  /**
   * Rendered inside the trade thread itself (rail, sheet): an open case shows
   * nothing here, since the case is the thread the reader is in (change 2).
   * Off-thread callers (the Contact modal) keep the status row with its link.
   */
  inThread?: boolean;
}

/**
 * Summon Admin button states (Decision 14, change 2). No case: the black
 * button opens the wizard. Open case: nothing in the thread, a status row
 * linking to the trade thread elsewhere. Resolved or closed case: the button
 * returns, and submitting reopens that case.
 */
export const SummonAdminAction: React.FC<Props> = ({ caseState, onSummon, disabled, layout = 'stack', inThread = false }) => {
  const open = !!caseState && !isTerminalCaseStatus(caseState.status.toUpperCase() as CaseStatus);

  if (open && caseState) {
    if (inThread) return null;
    const number = formatCaseNumber(caseState.caseNumber);
    const label = number ? `Case ${number} open` : 'Case open';
    return (
      <div className={`summon-admin-status ${layout}`} role="status">
        <span className="summon-admin-status-text">{label}</span>
        {caseState.conversationId ? (
          <>
            <span className="summon-admin-status-sep" aria-hidden="true">·</span>
            <Link to={`/inbox/${caseState.conversationId}`} className="summon-admin-status-link">
              View trade thread
            </Link>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`summon-admin-button ${layout}`}>
      <MagicButton label="Summon Admin…" color="black" onPress={onSummon} isDisabled={disabled} fullWidth={layout === 'stack'} />
    </div>
  );
};

export default SummonAdminAction;
